/**
 * TimecardControl — compact clock-in / clock-out widget for dashboard headers.
 *
 * Activity tracking design:
 *  • Reads getLastActivityMs() from use-session-tracker (shared registry
 *    updated on every real user interaction) — no duplicate event listeners.
 *  • Sends a periodic POST /api/timecard/me/activity while clocked in so the
 *    server's last_activity_at column stays current.  Only pings if the user
 *    was genuinely active since the last ping (guards via getLastActivityMs).
 *    This makes the sweeper / midnight reset authoritative.
 *  • For idle clock-out, sends the exact getLastActivityMs() value as
 *    `lastActivityAt`; the server validates and uses it.
 *  • There is intentionally NO pagehide/tab-close auto-clock-out: pagehide
 *    fires on ordinary reloads/navigations too, so it would close a shift the
 *    instant a user refreshed. Abandoned shifts are closed by the 15-minute
 *    idle timeout and the server-side forgot-clockout sweeper instead.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, LogIn, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { apiRequest, readCsrfCookie } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { TIMECARD_IDLE_WINDOW_MS } from "@/lib/timecard-tunables";
import { getLastActivityMs } from "@/hooks/use-session-tracker";

/** How often (ms) to push an activity ping to /api/timecard/me/activity. */
const ACTIVITY_PING_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

type TimecardState = {
  enabled: boolean;
  openShift: {
    id: string;
    clockInAt: string;
    lastActivityAt: string | null;
  } | null;
  todayShifts: Array<{ clockOutAt: string | null; clockOutSource?: string | null }>;
  totalClockedMs: number;
  todayStr: string;
};

function formatElapsed(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

interface TimecardControlProps {
  isPlatformWorkspace?: boolean;
  onIdleClockOut?: () => void;
}

export function TimecardControl({ isPlatformWorkspace, onIdleClockOut }: TimecardControlProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activityPingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPingedActivityMs = useRef<number>(0);

  const { data: state, isLoading } = useQuery<TimecardState>({
    queryKey: ["/api/timecard/me/state"],
    enabled: !isPlatformWorkspace,
    refetchInterval: 60_000,
  });

  const enableMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/timecard/me/settings", { enabled: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/timecard/me/state"] });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't enable time card", description: err?.message, variant: "destructive" });
    },
  });

  const clockInMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/timecard/me/clock-in"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/timecard/me/state"] });
      // Refresh the manager Shifts list (Team Activity → Timecards → Shifts)
      // so a freshly started shift appears without a page reload.
      qc.invalidateQueries({ queryKey: ["/api/admin/timecard/shifts"] });
      toast({ title: "Clocked in", description: "Your shift has started." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to clock in", description: err?.message, variant: "destructive" });
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: (payload: { source: string; lastActivityAt?: string }) =>
      apiRequest("POST", "/api/timecard/me/clock-out", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/timecard/me/state"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/timecard/shifts"] });
      toast({ title: "Clocked out", description: "Shift ended." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to clock out", description: err?.message, variant: "destructive" });
    },
  });

  // ── Live elapsed timer ────────────────────────────────────────────────────
  useEffect(() => {
    if (!state?.openShift) {
      if (timerRef.current) clearInterval(timerRef.current);
      setElapsed(0);
      return;
    }
    const clockInMs = new Date(state.openShift.clockInAt).getTime();
    const tick = () => setElapsed(Date.now() - clockInMs);
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [state?.openShift?.id]);

  // ── Periodic server-side activity ping ───────────────────────────────────
  // POSTs to /api/timecard/me/activity every ACTIVITY_PING_INTERVAL_MS while
  // a shift is open AND the user was genuinely active since the last ping.
  // This keeps last_activity_at server-authoritative so the sweeper and
  // midnight reset always close at the real last-interaction time, not
  // the stale clockInAt fallback.
  useEffect(() => {
    if (!state?.openShift) {
      if (activityPingRef.current) clearInterval(activityPingRef.current);
      return;
    }

    // Reset on new shift
    lastPingedActivityMs.current = 0;

    const ping = async () => {
      const lastActivity = getLastActivityMs();
      // Only ping if the user was active since the last ping — avoids
      // bumping lastActivityAt when the tab is idle/backgrounded.
      if (lastActivity <= lastPingedActivityMs.current) return;
      lastPingedActivityMs.current = lastActivity;
      try {
        // Send the exact last-interaction timestamp so the server persists
        // the real interaction instant, not the ping-arrival time.
        // /api/timecard/me/activity is NOT CSRF-exempt, so the X-CSRF-Token
        // header is required — without it the ping is rejected (403) and
        // last_activity_at silently never updates.
        const token = readCsrfCookie();
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (token) headers["X-CSRF-Token"] = token;
        await fetch("/api/timecard/me/activity", {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({
            lastActivityAt: new Date(lastActivity).toISOString(),
          }),
        });
      } catch {
        // best-effort; no retry needed — next tick will re-attempt
      }
    };

    // Send an initial ping immediately when a shift opens/mounts
    void ping();
    activityPingRef.current = setInterval(ping, ACTIVITY_PING_INTERVAL_MS);
    return () => { if (activityPingRef.current) clearInterval(activityPingRef.current); };
  }, [state?.openShift?.id]);

  // ── Idle auto-clock-out (check every 30s) ────────────────────────────────
  // Reads getLastActivityMs() from the shared use-session-tracker registry —
  // no duplicate event listeners.  Sends the exact last-interaction instant
  // as `lastActivityAt` so the server stamps at true last click, not detect time.
  useEffect(() => {
    if (!state?.openShift) {
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
      return;
    }

    const check = () => {
      const lastActivity = getLastActivityMs();
      const idle = Date.now() - lastActivity;
      if (idle >= TIMECARD_IDLE_WINDOW_MS) {
        clockOutMutation.mutate({
          source: "idle_timeout",
          lastActivityAt: new Date(lastActivity).toISOString(),
        });
        onIdleClockOut?.();
        if (idleTimerRef.current) clearInterval(idleTimerRef.current);
      }
    };

    idleTimerRef.current = setInterval(check, 30_000);
    return () => { if (idleTimerRef.current) clearInterval(idleTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.openShift?.id]);

  // ── No tab-close / pagehide auto-clock-out ───────────────────────────────
  // We intentionally do NOT clock out on `pagehide`. The browser fires that
  // event on ordinary in-app reloads and full-page navigations — not just real
  // tab/browser closes — so a user who simply refreshed (e.g. to check the
  // Shifts list) would have their just-started shift instantly closed, which is
  // exactly the "shift ends ~seconds after clock-in" bug. A keepalive request
  // whose body is dropped during unload also lands as a source-less close.
  // Abandoned shifts are still closed by the legitimate auto-close rules: the
  // 15-minute client idle timeout and the server-side forgot-clockout sweeper,
  // both of which stamp the real last-activity time.

  if (isPlatformWorkspace) return null;

  if (!isLoading && !state?.enabled) {
    return (
      <div className="flex items-center gap-2" data-testid="timecard-enable-row">
        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
        <Label htmlFor="timecard-enable-switch" className="text-xs text-muted-foreground cursor-pointer select-none">
          Time Card
        </Label>
        <Switch
          id="timecard-enable-switch"
          checked={false}
          disabled={enableMutation.isPending}
          onCheckedChange={() => enableMutation.mutate()}
          data-testid="switch-timecard-enable"
        />
      </div>
    );
  }

  if (isLoading) return null;

  const isIn = Boolean(state.openShift);
  const totalMs = state.totalClockedMs + (isIn ? elapsed : 0);

  return (
    <div className="flex items-center gap-2" data-testid="timecard-control">
      {isIn && (
        <Badge
          variant="outline"
          className="gap-1 bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 font-mono text-xs"
          data-testid="badge-timecard-elapsed"
        >
          <Clock className="w-3 h-3" />
          {formatElapsed(elapsed)}
        </Badge>
      )}
      {!isIn && totalMs > 0 && (
        <span className="text-xs text-muted-foreground font-mono" data-testid="text-timecard-today">
          {formatElapsed(totalMs)} today
        </span>
      )}
      <Button
        size="sm"
        variant={isIn ? "outline" : "default"}
        className={cn(
          "gap-1.5 h-8 text-xs",
          isIn && "border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10",
        )}
        onClick={() => {
          if (isIn) {
            clockOutMutation.mutate({ source: "manual" });
          } else {
            clockInMutation.mutate();
          }
        }}
        disabled={clockInMutation.isPending || clockOutMutation.isPending}
        data-testid={isIn ? "button-clock-out" : "button-clock-in"}
      >
        {isIn ? (
          <><LogOut className="w-3.5 h-3.5" /> Clock Out</>
        ) : (
          <><LogIn className="w-3.5 h-3.5" /> Clock In</>
        )}
      </Button>
    </div>
  );
}
